import CacheByArgumentStrings from '../mem/cache-by-argument-strings';
import Signal from '../control-flow/signal';
import assets from '../assets';
import traverse from './traverse-generator';
import { Color, FrontSide, ImageBitmapLoader, LoadingManager, Material, Mesh, MeshBasicMaterial, MeshPhysicalMaterial, MeshStandardMaterial, Object3D, RepeatWrapping, SRGBColorSpace, Texture } from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { assign, clone } from "../json/optional-properties";
import { basename } from '../path/basename';
import logging from '../logging';

const log = logging('view3d');

/**
 * A one pixel white PNG encoded as a data URL
 */
const defaultTextureDataUrl = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABAQMAAAAl21bKAAAAA1BMVEX///+nxBvIAAAACklEQVQI12NgAAAAAgAB4iG8MwAAAABJRU5ErkJggg==";

const isMesh = (obj: Object3D): obj is Mesh => {
    return obj && obj["isMesh"];
}

/**
 * The MaterialProperties type must have an `assetId` string, and it may have a
 * `color` or `tint`. This allows us to accept a Material domain model instance
 * or just a hard coded `{assetId: 'materials/...'}` string.
 */
export type MaterialProperties = {
    assetId: string,
    color?: string,
    tint?: string,
};

export class GLTFMaterialLoader {

    private gltfLoader: GLTFLoader;
    private imageBitmapLoader: ImageBitmapLoader;
    private textureCache = new CacheByArgumentStrings<Texture>();
    private materialCache =
        new CacheByArgumentStrings<MeshStandardMaterial | MeshPhysicalMaterial>();

    private defaultColor = new Texture();

    onMaterialUpdate = new Signal<[updated: Material, source: Material]>('onMaterialUpdated', 'loader');

    /**
     */
    constructor(manager: LoadingManager) {
        this.gltfLoader = new GLTFLoader(manager);
        this.imageBitmapLoader = new ImageBitmapLoader(manager);
        this.imageBitmapLoader.setOptions({ imageOrientation: "flipY" });
    }

    /**
     * @param model - An instance of the Material domain model, or simply an
     * object with an assetId property.
     */
    public load(model: MaterialProperties): Material {
        let result = this.materialCache.get(model.assetId, model.color, model.tint);
        if(result) {
            return result;
        } else {
            result = new MeshStandardMaterial({side: FrontSide});
            this.materialCache.put(result, model.assetId, model.color, model.tint)

            result.userData.materialAssetId = model.assetId;
            const url = assets.resolve(model.assetId, 'OnlineMaterial');

            // We initiate loading this texture now rather than waiting until
            // the glTF has been loaded.
            let
                color: Texture,
                digits: string = null;
            if(model.color) {
               if(model.color.startsWith('color-maps/')) {
                   digits = model.color.substring(11);
                   color = this.loadImageDataTexture(
                       assets.resolve(`color-maps/${digits}`, 'ColorMap'));
               } else {
                   color = this.loadImageDataTexture(model.color);
               }
            }

            this.loadMaterial(url)
                .then((mat: MeshStandardMaterial | MeshPhysicalMaterial) => {
                    // Save a copy of the userData, which may have been
                    // modified while we were waiting for the glTF file to load.
                    const userData = result.userData;

                    // If the GLB file has a MeshPhysicalMaterial, then we need
                    // to update `result` to be an instance of that class.
                    if(mat instanceof MeshPhysicalMaterial) {
                        Object.assign(result, mat);
                        Object.setPrototypeOf(result, MeshPhysicalMaterial.prototype);
                    }
                    else if(mat instanceof MeshBasicMaterial) {
                        for(const name of Object.getOwnPropertyNames(result)) {
                            const desc = Object.getOwnPropertyDescriptor(result, name);
                            if(desc.writable) {
                                delete result[name];
                            }
                        }
                        Object.assign(result, mat);
                        Object.setPrototypeOf(result, MeshBasicMaterial.prototype);
                    }
                    result.copy(mat);

                    // Restore the user data
                    result.userData = clone(userData);
                    assign(result.userData, mat.userData);

                    if(color) {
                        result.map = color;
                        if(digits) result.userData.materialColormap = digits;
                    }
                    if(model.tint) {
                        result.color = new Color(model.tint);
                        result.userData.materialTint = model.tint;
                    }

                    result.side = FrontSide;


                    // because result was cached even before loading completed,
                    // every mesh using the assetId should get the same
                    // material. So this should update all the meshes using
                    // result at once.
                    result.needsUpdate = true;

                    this.onMaterialUpdate.trigger(result, mat);
                })
                .catch(console.error);
            return result;
        }
    };

    /**
     * Used internally to do the actual loading of the material and upgrading.
     */
    private loadMaterial(url: string): Promise<Material> {
        let result: Material = this.materialCache.get(url);
        if(result) {
            return Promise.resolve(result);
        } else {
            return new Promise((resolve, reject) => {
                this.gltfLoader.load(
                    url,
                    gltf => {
                        for(const obj of traverse(gltf.scene)) {
                            if(isMesh(obj)) {
                                resolve(obj.material as Material);
                                return;
                            }
                        }
                        // If we get here, we didn't find a material
                        reject(`Loaded glTF but did not find a material. Url: "${url}"`);
                    },
                    null,
                    reject,
                );
            });
        }
    }

    /**
     * Used intertally to load color PNG files for materials like
     * standard-melamine.
     */
    private loadImageBitmap(url: string): Promise<ImageBitmap> {
        return new Promise((resolve, reject) => {
            this.imageBitmapLoader.load(
                url,
                resolve,
                null,
                reject,
            );
        })
    }

    /**
     * Used intertally to load color PNG files for materials like
     * standard-melamine.
     *
     * @param url - The URL of the image file to load
     * @returns a default Texture that will eventually be assigned texture data
     * from the given URL or will log an error if the ImageBitmapLoader is
     * unable to load the texture data.
     */
    private loadImageDataTexture(url: string): Texture {
        let result: Texture = this.textureCache.get(url);
        if(result) {
            return result;
        } else {
            result = new Texture();
            result.wrapS = result.wrapT = RepeatWrapping;
            result.colorSpace = SRGBColorSpace;
            this.textureCache.put(result, url);
            this.loadImageBitmap(url)
                .then(img => {
                    result.image = img;
                    result.needsUpdate = true;
                })
                .catch(error => {
                    log.error('gltf-material-loader falling back to white texture for color', error);
                    this.loadImageBitmap(defaultTextureDataUrl)
                        .then(img => {
                            result.image = img;
                            result.needsUpdate = true;
                        })
                        .catch(log.error);
                });

            return result;
        }
    };
}
export default GLTFMaterialLoader;
